import gql from 'graphql-tag';

/**
 * Non-authenticated (public) resolvers on MembershipDS.
 */
export const PublicGraphQL = {
  Query: {
    IS_USERNAME_AVAILABLE: gql`
      query IsUsernameAvailable($tenant: String!, $login: String!) {
        isUsernameAvailable(tenant: $tenant, login: $login)
      }
    `,
    IS_EMAIL_VERIFIED: gql`
      query IsEmailVerified($tenant: String!, $email: String!) {
        isEmailVerified(tenant: $tenant, email: $email)
      }
    `,
    CAN_RESET_PASSWORD: gql`
      query CanResetPassword($tenant: String!, $login: String!) {
        canResetPassword(tenant: $tenant, login: $login) {
          eligible
          user {
            username
            firstname
            lastname
            email
            verifiedemail
          }
          parent {
            firstname
            lastname
            email
          }
        }
      }
    `,
    REQUEST_RESET_PASSWORD: gql`
      query RequestResetPassword(
        $locationOrigin: String!
        $tenant: String!
        $username: String!
      ) {
        requestResetPassword(
          locationOrigin: $locationOrigin
          tenant: $tenant
          username: $username
        )
      }
    `,
    REQUEST_LINKED_ACCOUNT_RESET_PASSWORD: gql`
      query RequestLinkedAccountResetPassword(
        $request: LinkedAccountResetPasswordRequest!
      ) {
        requestLinkedAccountResetPassword(request: $request)
      }
    `,
    PROFILE: gql`
      query Profile($tenant: String!) {
        profile(tenant: $tenant) {
          position
          required
          profilekey
          title
          description
          fields {
            title
            profilekey
            fieldposition
            type
            required
            items {
              itemposition
              title
            }
          }
        }
      }
    `,
    INVITATION_PARAMS: gql`
      query InvitationParams($token: String!) {
        invitationParams(token: $token) {
          tenant
          email
          users {
            userkey
            username
            email
          }
        }
      }
    `
  },
  Mutation: {
    REGISTER: gql`
      mutation Register(
        $locationOrigin: String!
        $tenant: String!
        $input: RegistrationRequestInput!
        $profileFieldInputs: [ProfileFieldInput]
      ) {
        register(
          locationOrigin: $locationOrigin
          tenant: $tenant
          input: $input
          profileFieldInputs: $profileFieldInputs
        ) {
          userkey
          emailVerificationRequired
        }
      }
    `,
    RESET_PASSWORD: gql`
      mutation ResetPassword($token: String!, $password: String!) {
        resetPassword(token: $token, password: $password)
      }
    `,
    ACCEPT_INVITATION: gql`
      mutation AcceptInvitation($token: String!, $userkey: Int) {
        acceptInvitation(token: $token, userkey: $userkey)
      }
    `,
    REQUEST_VERIFICATION_EMAIL: gql`
      mutation RequestVerificationEmail(
        $locationOrigin: String!
        $tenant: String!
        $username: String!
      ) {
        requestVerificationEmail(
          locationOrigin: $locationOrigin
          tenant: $tenant
          username: $username
        )
      }
    `,
    VERIFY_RECAPTCHA_TOKEN: gql`
      mutation VerifyRecaptchaToken($token: String!) {
        verifyRecaptchaToken(token: $token)
      }
    `,
    REQUEST_ADD_EMAIL: gql`
      mutation RequestAddEmail(
        $locationOrigin: String!
        $tenant: String!
        $username: String!
        $password: String!
        $email: String!
      ) {
        requestAddEmail(
          locationOrigin: $locationOrigin
          tenant: $tenant
          username: $username
          password: $password
          email: $email
        )
      }
    `
  }
};

/**
 * Authenticated (protected) resolvers on MembershipDS.
 */
const GraphQL = {
  Query: {
    VERIFIED_EMAIL: gql`
      query VerifiedEmail($tenant: String!, $login: String!) {
        verifiedEmail(tenant: $tenant, login: $login)
      }
    `,
    VERIFY_EMAIL: gql`
      query VerifyEmail($token: String!) {
        verifyEmail(token: $token) {
          userkey
          username
          email
          firstname
          verifiedemail
        }
      }
    `,
    ZONES: gql`
      query Zones($multicode: String!) {
        zones(multicode: $multicode) {
          name
        }
      }
    `,
    USER: gql`
      query User($tenant: String!, $userId: String!) {
        user(tenant: $tenant, userId: $userId) {
          userkey
          username
          verifiedemail
        }
      }
    `,
    USER_BY_KEY: gql`
      query UserByKey($userkey: Int!) {
        userByKey(userkey: $userkey) {
          userkey
          username
          firstname
          lastname
          email
          verifiedemail
          masteraccess
          parentkey
          authoraccess
        }
      }
    `,
    USERS: gql`
      query Users($tenant: String!, $email: String!) {
        user(tenant: $tenant, email: $email) {
          userkey
          username
          email
          verifiedemail
        }
      }
    `,
    ALL_USERS: gql`
      query AllUsers {
        allUsers {
          userkey
          username
          firstname
          lastname
          email
          verifiedemail
          masteraccess
        }
      }
    `,
    AUTH: gql`
      query Auth($tenant: String!, $username: String!, $password: String!) {
        auth(tenant: $tenant, username: $username, password: $password)
      }
    `,
    USER_PROFILE_FIELDS: gql`
      query UserProfileFields($userkey: Int!) {
        userProfileFields(userkey: $userkey) {
          profilekey
          fieldposition
          value
        }
      }
    `,
    INVITATIONS_FOR_GROUP: gql`
      query InvitationsForGroup($groupkey: Int!) {
        invitationsForGroup(groupkey: $groupkey) {
          token
          email
          timestamp
          accepted
        }
      }
    `,
    GET_SITEADMIN_INVITES: gql`
      query GetSiteAdminInvites {
        getSiteAdminInvites {
          token
          email
          timestamp
          accepted
        }
      }
    `
  },

  Mutation: {
    UPDATE_PASSWORD: gql`
      mutation UpdatePassword(
        $tenant: String!
        $username: String!
        $password: String!
      ) {
        updatePassword(
          tenant: $tenant
          username: $username
          password: $password
        )
      }
    `,
    REQUEST_EMAIL_UPDATE: gql`
      mutation RequestEmailUpdate(
        $locationOrigin: String!
        $tenant: String!
        $userkey: Int!
        $email: String!
        $requireVerification: Boolean
      ) {
        requestEmailUpdate(
          locationOrigin: $locationOrigin
          tenant: $tenant
          userkey: $userkey
          email: $email
          requireVerification: $requireVerification
        )
      }
    `,
    ADD_GROUP_MEMBER: gql`
      mutation AddGroupMember(
        $tenant: String!
        $groupkey: Int!
        $groupName: String!
        $emailOrUsername: String!
        $inviterUserkey: Int
        $locationOrigin: String
      ) {
        addGroupMember(
          tenant: $tenant
          groupkey: $groupkey
          groupName: $groupName
          emailOrUsername: $emailOrUsername
          inviterUserkey: $inviterUserkey
          locationOrigin: $locationOrigin
        )
      }
    `,
    UNVERIFY_EMAIL_ADDRESS: gql`
      mutation UnverifyEmailAddress($userkey: Int!) {
        unverifyEmailAddress(userkey: $userkey) {
          userkey
        }
      }
    `,
    UPDATE_USER_PROFILE_FIELD: gql`
      mutation UpdateUserProfileField(
        $profilekey: Int!
        $fieldposition: Int!
        $userkey: Int!
        $value: String!
      ) {
        updateUserProfileField(
          profilekey: $profilekey
          fieldposition: $fieldposition
          userkey: $userkey
          value: $value
        ) {
          profilekey
          fieldposition
          value
        }
      }
    `,
    UPDATE_USER_PROFILE_FIELDS: gql`
      mutation UpdateUserProfileFields(
        $userkey: Int!
        $profileFieldInputs: [ProfileFieldInput]
      ) {
        updateUserProfileFields(
          userkey: $userkey
          profileFieldInputs: $profileFieldInputs
        ) {
          profilekey
          fieldposition
          value
        }
      }
    `,
    UPDATE_USER_NAMES: gql`
      mutation UpdateUserNames($request: UpdateUserNamesRequest!) {
        updateUserNames(request: $request) {
          userkey
          username
          firstname
          lastname
          email
        }
      }
    `,
    DELETE_USER: gql`
      mutation DeleteUser($tenant: String!, $username: String!) {
        deleteUser(tenant: $tenant, username: $username)
      }
    `,
    SAVE_PROFILE: gql`
      mutation SaveProfile($tenant: String!, $profile: ProfileUpdate!) {
        saveProfile(tenant: $tenant, profile: $profile) {
          profilekey
        }
      }
    `,
    LESSON_LINK_EMAIL: gql`
      mutation LessonLinkEmail(
        $tenant: String!
        $userkey: Int!
        $link: String!
        $email: String!
      ) {
        lessonLinkEmail(
          tenant: $tenant
          userkey: $userkey
          link: $link
          email: $email
        )
      }
    `,
    DELETE_SENT_INVITATION: gql`
      mutation DeleteSentInvitation($token: String!) {
        deleteSentInvitation(token: $token)
      }
    `,
    VERIFY_OR_UNVERIFY_EMAIL_MANUALLY: gql`
      mutation VerifyOrUnverifyEmailManually($userkey: Int!, $email: String) {
        verifyOrUnverifyEmailManually(userkey: $userkey, email: $email) {
          userkey
        }
      }
    `,
    ADMIN_INVITE_USERS_BY_EMAIL: gql`
      mutation AdminInviteUsersByEmail(
        $locationOrigin: String!
        $tenant: String!
        $emails: [String]!
      ) {
        adminInviteUsersByEmail(
          locationOrigin: $locationOrigin
          tenant: $tenant
          emails: $emails
        )
      }
    `
  }
};

export default GraphQL;
